import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useIsAdmin, useIsAccountant } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import {
  Plus,
  Search,
  MoreVertical,
  Eye,
  Edit,
  Trash2,
  FileText,
  Download,
  ArrowRightCircle,
  ClipboardList,
  Package,
  Building2,
  Calendar,
  X,
  Copy,
  CheckCircle2,
  ListChecks
} from 'lucide-react';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  draft: 'bg-slate-100 text-slate-700 border-slate-200',
  sent: 'bg-blue-100 text-blue-700 border-blue-200',
  partially_received: 'bg-amber-100 text-amber-700 border-amber-200',
  fully_received: 'bg-green-100 text-green-700 border-green-200',
  cancelled: 'bg-red-100 text-red-700 border-red-200'
};

const statusLabels = {
  draft: 'Draft',
  sent: 'Sent',
  partially_received: 'Partial',
  fully_received: 'Received',
  cancelled: 'Cancelled'
};

const PurchaseOrders = () => {
  const isAdmin = useIsAdmin();
  const isAccountant = useIsAccountant();
  
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);
  const [editMode, setEditMode] = useState(false);
  
  // Vendor invoice selection states (NEW)
  const [vendorInvoices, setVendorInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [importMode, setImportMode] = useState(null); // 'full' | 'partial' | null
  const [partialSelections, setPartialSelections] = useState({});
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    vendor_id: '',
    po_date: new Date().toISOString().split('T')[0],
    items: [],
    notes: ''
  });
  
  // Convert form state
  const [convertData, setConvertData] = useState({
    vendor_invoice_number: '',
    purchase_date: new Date().toISOString().split('T')[0],
    items: []
  });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchData();
  }, []);

  // Fetch vendor invoices when vendor changes (NEW)
  useEffect(() => {
    if (formData.vendor_id && !editMode) {
      fetchVendorInvoices(formData.vendor_id);
    } else {
      setVendorInvoices([]);
      setSelectedInvoice(null);
      setImportMode(null);
    }
  }, [formData.vendor_id, editMode]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [posRes, vendorsRes, productsRes] = await Promise.all([
        axios.get(`${API_URL}/api/purchase-orders`, { headers }),
        axios.get(`${API_URL}/api/vendors`, { headers }),
        axios.get(`${API_URL}/api/products`, { headers })
      ]);
      setPurchaseOrders(posRes.data);
      setVendors(vendorsRes.data);
      setProducts(productsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch and group invoices for selected vendor (NEW)
  const fetchVendorInvoices = async (vendorId) => {
    setLoadingInvoices(true);
    try {
      const response = await axios.get(`${API_URL}/api/purchases?vendor_id=${vendorId}`, { headers });
      const purchases = response.data;
      
      // Group purchases by vendor_invoice_number
      const invoiceMap = {};
      purchases.forEach(p => {
        const key = p.vendor_invoice_number;
        if (!invoiceMap[key]) {
          invoiceMap[key] = {
            invoice_number: key,
            date: p.purchase_date,
            items: []
          };
        }
        invoiceMap[key].items.push({
          product_id: p.product_id,
          product_name: p.product_name,
          quantity: p.quantity,
          purchase_price: p.purchase_price,
          gst_percentage: p.gst_percentage || 12
        });
      });
      
      // Convert to array and sort by date (newest first)
      const invoices = Object.values(invoiceMap).sort((a, b) => 
        new Date(b.date) - new Date(a.date)
      );
      
      setVendorInvoices(invoices);
    } catch (error) {
      console.error('Failed to fetch vendor invoices:', error);
      setVendorInvoices([]);
    } finally {
      setLoadingInvoices(false);
    }
  };

  // Handle invoice selection (NEW)
  const handleInvoiceSelect = (invoiceNumber) => {
    const invoice = vendorInvoices.find(inv => inv.invoice_number === invoiceNumber);
    setSelectedInvoice(invoice);
    setImportMode(null);
    setPartialSelections({});
  };

  // Handle import mode selection (NEW)
  const handleImportModeSelect = (mode) => {
    setImportMode(mode);
    if (mode === 'full' && selectedInvoice) {
      // Auto-populate all items from invoice
      populateItemsFromInvoice(selectedInvoice.items);
    } else if (mode === 'partial' && selectedInvoice) {
      // Initialize partial selections with all items unselected
      const selections = {};
      selectedInvoice.items.forEach((item, idx) => {
        selections[idx] = {
          selected: false,
          quantity: item.quantity
        };
      });
      setPartialSelections(selections);
    }
  };

  // Populate form items from invoice items (NEW)
  const populateItemsFromInvoice = (invoiceItems) => {
    // Aggregate quantities for same product
    const productMap = {};
    invoiceItems.forEach(item => {
      if (productMap[item.product_id]) {
        productMap[item.product_id].quantity += item.quantity;
      } else {
        const product = products.find(p => p.id === item.product_id);
        productMap[item.product_id] = {
          product_id: item.product_id,
          product_name: item.product_name || product?.name || 'Unknown Product',
          brand: product?.brand || '',
          size: product?.size || '',
          hsn_code: product?.hsn_code || '',
          quantity: item.quantity,
          rate: item.purchase_price || product?.purchase_price || '',
          gst_percentage: item.gst_percentage || product?.gst_percentage || 12
        };
      }
    });
    
    setFormData(prev => ({
      ...prev,
      items: Object.values(productMap)
    }));
    
    // Reset invoice selection UI
    setSelectedInvoice(null);
    setImportMode(null);
    setPartialSelections({});
    
    toast.success(`Added ${Object.keys(productMap).length} products from invoice`);
  };

  // Handle partial selection checkbox toggle (NEW)
  const togglePartialSelection = (idx) => {
    setPartialSelections(prev => ({
      ...prev,
      [idx]: {
        ...prev[idx],
        selected: !prev[idx].selected
      }
    }));
  };

  // Handle partial selection quantity change (NEW)
  const updatePartialQuantity = (idx, quantity) => {
    setPartialSelections(prev => ({
      ...prev,
      [idx]: {
        ...prev[idx],
        quantity: parseInt(quantity) || 1
      }
    }));
  };

  // Apply partial selections to form (NEW)
  const applyPartialSelections = () => {
    if (!selectedInvoice) return;
    
    const selectedItems = selectedInvoice.items
      .filter((_, idx) => partialSelections[idx]?.selected)
      .map((item, idx) => {
        const originalIdx = selectedInvoice.items.indexOf(item);
        return {
          ...item,
          quantity: partialSelections[originalIdx]?.quantity || item.quantity
        };
      });
    
    if (selectedItems.length === 0) {
      toast.error('Please select at least one item');
      return;
    }
    
    populateItemsFromInvoice(selectedItems);
  };

  // Cancel invoice import (NEW)
  const cancelInvoiceImport = () => {
    setSelectedInvoice(null);
    setImportMode(null);
    setPartialSelections({});
  };

  const filteredPOs = useMemo(() => {
    return purchaseOrders.filter(po => {
      const matchesSearch = 
        po.po_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        po.vendor_name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || po.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [purchaseOrders, searchTerm, statusFilter]);

  const handleCreatePO = async () => {
    if (!formData.vendor_id) {
      toast.error('Please select a vendor');
      return;
    }
    if (formData.items.length === 0) {
      toast.error('Please add at least one item');
      return;
    }

    try {
      const payload = {
        vendor_id: formData.vendor_id,
        po_date: formData.po_date,
        items: formData.items.map(item => ({
          product_id: item.product_id,
          quantity: parseInt(item.quantity),
          rate: item.rate ? parseFloat(item.rate) : null,
          gst_percentage: item.gst_percentage ? parseFloat(item.gst_percentage) : 12
        })),
        notes: formData.notes
      };

      if (editMode && selectedPO) {
        await axios.put(`${API_URL}/api/purchase-orders/${selectedPO.id}`, payload, { headers });
        toast.success('Purchase Order updated');
      } else {
        await axios.post(`${API_URL}/api/purchase-orders`, payload, { headers });
        toast.success('Purchase Order created');
      }

      setShowCreateModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save Purchase Order');
    }
  };

  const handleDeletePO = async (po) => {
    if (!window.confirm(`Delete PO ${po.po_number}?`)) return;

    try {
      await axios.delete(`${API_URL}/api/purchase-orders/${po.id}`, { headers });
      toast.success('Purchase Order deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleStatusChange = async (po, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/purchase-orders/${po.id}/status?status=${newStatus}`, {}, { headers });
      toast.success(`Status updated to ${statusLabels[newStatus]}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleDownloadPDF = async (po) => {
    try {
      const response = await axios.get(`${API_URL}/api/purchase-orders/${po.id}/download-pdf`, {
        headers,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `PO_${po.po_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const handleConvertToPurchase = async () => {
    if (!convertData.vendor_invoice_number) {
      toast.error('Please enter vendor invoice number');
      return;
    }
    if (convertData.items.length === 0 || convertData.items.some(i => !i.batch_number || !i.purchase_price)) {
      toast.error('Please fill all item details');
      return;
    }

    try {
      const payload = {
        vendor_invoice_number: convertData.vendor_invoice_number,
        purchase_date: convertData.purchase_date,
        items: convertData.items.map(item => ({
          product_id: item.product_id,
          batch_number: item.batch_number,
          quantity: parseInt(item.quantity),
          purchase_price: parseFloat(item.purchase_price),
          gst_percentage: parseFloat(item.gst_percentage || 12)
        }))
      };

      const response = await axios.post(`${API_URL}/api/purchase-orders/${selectedPO.id}/convert`, payload, { headers });
      toast.success(`Created ${response.data.items_count} purchase entries`);
      setShowConvertModal(false);
      setConvertData({ vendor_invoice_number: '', purchase_date: new Date().toISOString().split('T')[0], items: [] });
      setSelectedPO(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert PO');
    }
  };

  const openViewModal = (po) => {
    setSelectedPO(po);
    setShowViewModal(true);
  };

  const openEditModal = (po) => {
    setSelectedPO(po);
    setEditMode(true);
    setFormData({
      vendor_id: po.vendor_id,
      po_date: po.po_date,
      items: po.items.map(item => ({
        product_id: item.product_id,
        product_name: item.product_name,
        brand: item.brand || '',
        size: item.size || '',
        quantity: item.quantity
      })),
      notes: po.notes || ''
    });
    setShowCreateModal(true);
  };

  const openConvertModal = (po) => {
    setSelectedPO(po);
    setConvertData({
      vendor_invoice_number: '',
      purchase_date: new Date().toISOString().split('T')[0],
      items: po.items.filter(item => item.received_quantity < item.quantity).map(item => ({
        product_id: item.product_id,
        product_name: item.product_name,
        batch_number: '',
        quantity: item.quantity - (item.received_quantity || 0),
        purchase_price: '',
        gst_percentage: 12
      }))
    });
    setShowConvertModal(true);
  };

  const resetForm = () => {
    setFormData({
      vendor_id: '',
      po_date: new Date().toISOString().split('T')[0],
      items: [],
      notes: ''
    });
    setSelectedPO(null);
    setEditMode(false);
    setVendorInvoices([]);
    setSelectedInvoice(null);
    setImportMode(null);
    setPartialSelections({});
  };

  const addItemToForm = (productId) => {
    const product = products.find(p => p.id === productId);
    if (!product) return;
    if (formData.items.find(i => i.product_id === productId)) {
      toast.error('Product already added');
      return;
    }
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, {
        product_id: product.id,
        product_name: product.name,
        brand: product.brand,
        size: product.size,
        hsn_code: product.hsn_code || '',
        quantity: 1,
        rate: product.purchase_price || '',
        gst_percentage: product.gst_percentage || 12
      }]
    }));
  };

  const updateItemQuantity = (productId, quantity) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map(item =>
        item.product_id === productId ? { ...item, quantity: parseInt(quantity) || 1 } : item
      )
    }));
  };

  const updateItemField = (productId, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map(item =>
        item.product_id === productId ? { ...item, [field]: value } : item
      )
    }));
  };

  const removeItemFromForm = (productId) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter(item => item.product_id !== productId)
    }));
  };

  const updateConvertItem = (productId, field, value) => {
    setConvertData(prev => ({
      ...prev,
      items: prev.items.map(item =>
        item.product_id === productId ? { ...item, [field]: value } : item
      )
    }));
  };

  // Format date for display (NEW)
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Purchase Orders</h1>
          <p className="text-sm text-slate-500 mt-1">Manage and track your purchase orders</p>
        </div>
        {isAdmin && (
          <Button
            onClick={() => { resetForm(); setShowCreateModal(true); }}
            className="bg-teal-600 hover:bg-teal-700"
            data-testid="create-po-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Purchase Order
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by PO number or vendor..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="po-search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48" data-testid="status-filter">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="partially_received">Partially Received</SelectItem>
                <SelectItem value="fully_received">Fully Received</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* PO List */}
      <Card>
        <CardContent className="p-0">
          {filteredPOs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-slate-500">
              <ClipboardList className="w-12 h-12 mb-4 text-slate-300" />
              <p className="text-lg font-medium">No purchase orders found</p>
              <p className="text-sm">Create a new PO to get started</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>PO Number</TableHead>
                    <TableHead>Vendor</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Items</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPOs.map((po) => (
                    <TableRow key={po.id} className="hover:bg-slate-50/50">
                      <TableCell>
                        <span className="font-mono text-sm font-semibold text-teal-700 bg-teal-50 px-2 py-1 rounded">
                          {po.po_number}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-slate-400" />
                          {po.vendor_name}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-slate-400" />
                          {new Date(po.po_date).toLocaleDateString()}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Package className="w-4 h-4 text-slate-400" />
                          {po.items.length} items
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={cn('text-xs', statusColors[po.status])}>
                          {statusLabels[po.status]}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" data-testid={`po-actions-${po.id}`}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openViewModal(po)}>
                              <Eye className="w-4 h-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDownloadPDF(po)}>
                              <Download className="w-4 h-4 mr-2" />
                              Download PDF
                            </DropdownMenuItem>
                            {isAdmin && po.status === 'draft' && (
                              <>
                                <DropdownMenuItem onClick={() => openEditModal(po)}>
                                  <Edit className="w-4 h-4 mr-2" />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleStatusChange(po, 'sent')}>
                                  <FileText className="w-4 h-4 mr-2" />
                                  Mark as Sent
                                </DropdownMenuItem>
                              </>
                            )}
                            {isAdmin && !['fully_received', 'cancelled'].includes(po.status) && (
                              <DropdownMenuItem onClick={() => openConvertModal(po)} className="text-teal-600">
                                <ArrowRightCircle className="w-4 h-4 mr-2" />
                                Convert to Purchase
                              </DropdownMenuItem>
                            )}
                            {isAdmin && po.status === 'draft' && (
                              <DropdownMenuItem onClick={() => handleStatusChange(po, 'cancelled')} className="text-red-600">
                                <X className="w-4 h-4 mr-2" />
                                Cancel PO
                              </DropdownMenuItem>
                            )}
                            {isAdmin && (
                              <DropdownMenuItem onClick={() => handleDeletePO(po)} className="text-red-600" data-testid={`po-delete-${po.id}`}>
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Modal - ENHANCED with Invoice Import */}
      <Dialog open={showCreateModal} onOpenChange={(open) => { if (!open) { resetForm(); } setShowCreateModal(open); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>{editMode ? 'Edit Purchase Order' : 'Create Purchase Order'}</DialogTitle>
            <DialogDescription>
              {editMode ? 'Update the purchase order details below.' : 'Select vendor and optionally import from previous invoices.'}
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="flex-1 px-1">
            <div className="space-y-6 py-4">
              {/* Step 1: Vendor & Date Selection */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Vendor *</Label>
                  <Select 
                    value={formData.vendor_id} 
                    onValueChange={(val) => setFormData(prev => ({ ...prev, vendor_id: val, items: [] }))}
                    disabled={editMode}
                  >
                    <SelectTrigger data-testid="po-vendor-select">
                      <SelectValue placeholder="Select vendor" />
                    </SelectTrigger>
                    <SelectContent>
                      {vendors.map(vendor => (
                        <SelectItem key={vendor.id} value={vendor.id}>{vendor.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>PO Date *</Label>
                  <Input
                    type="date"
                    value={formData.po_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, po_date: e.target.value }))}
                    data-testid="po-date-input"
                  />
                </div>
              </div>

              {/* Step 2: Invoice Selection - Shows only when vendor is selected and not in edit mode */}
              {formData.vendor_id && !editMode && formData.items.length === 0 && (
                <div className="space-y-4 p-4 bg-teal-50 border border-teal-200 rounded-lg">
                  <div className="flex items-center gap-2 text-teal-700">
                    <Copy className="w-5 h-5" />
                    <span className="font-medium">Quick Import from Previous Invoice</span>
                  </div>
                  
                  {loadingInvoices ? (
                    <div className="flex items-center justify-center py-4">
                      <div className="spinner" />
                      <span className="ml-2 text-sm text-slate-500">Loading invoices...</span>
                    </div>
                  ) : vendorInvoices.length === 0 ? (
                    <p className="text-sm text-slate-500 italic">No previous invoices found for this vendor. Add products manually below.</p>
                  ) : (
                    <div className="space-y-3">
                      {/* Invoice Dropdown */}
                      {!selectedInvoice && (
                        <div className="space-y-2">
                          <Label className="text-teal-700">Select Previous Invoice</Label>
                          <Select onValueChange={handleInvoiceSelect}>
                            <SelectTrigger data-testid="po-invoice-select" className="bg-white">
                              <SelectValue placeholder="Choose an invoice to import from..." />
                            </SelectTrigger>
                            <SelectContent>
                              {vendorInvoices.map((inv, idx) => (
                                <SelectItem key={idx} value={inv.invoice_number}>
                                  <span className="font-medium">{inv.invoice_number}</span>
                                  <span className="text-slate-500 ml-2">- {formatDate(inv.date)}</span>
                                  <span className="text-slate-400 ml-2">({inv.items.length} items)</span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      {/* Full/Partial Selection */}
                      {selectedInvoice && !importMode && (
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-slate-700">Invoice: {selectedInvoice.invoice_number}</p>
                              <p className="text-sm text-slate-500">{formatDate(selectedInvoice.date)} - {selectedInvoice.items.length} items</p>
                            </div>
                            <Button variant="ghost" size="sm" onClick={cancelInvoiceImport}>
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                          <p className="text-sm text-teal-700 font-medium">How would you like to import?</p>
                          <div className="flex gap-3">
                            <Button
                              variant="outline"
                              className="flex-1 h-auto py-3 border-teal-300 hover:bg-teal-100 hover:border-teal-400"
                              onClick={() => handleImportModeSelect('full')}
                              data-testid="import-full-btn"
                            >
                              <div className="flex flex-col items-center gap-1">
                                <CheckCircle2 className="w-5 h-5 text-teal-600" />
                                <span className="font-medium">Full</span>
                                <span className="text-xs text-slate-500">Import all items</span>
                              </div>
                            </Button>
                            <Button
                              variant="outline"
                              className="flex-1 h-auto py-3 border-teal-300 hover:bg-teal-100 hover:border-teal-400"
                              onClick={() => handleImportModeSelect('partial')}
                              data-testid="import-partial-btn"
                            >
                              <div className="flex flex-col items-center gap-1">
                                <ListChecks className="w-5 h-5 text-teal-600" />
                                <span className="font-medium">Partial</span>
                                <span className="text-xs text-slate-500">Select specific items</span>
                              </div>
                            </Button>
                          </div>
                        </div>
                      )}

                      {/* Partial Selection Table */}
                      {selectedInvoice && importMode === 'partial' && (
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <p className="font-medium text-slate-700">Select items from {selectedInvoice.invoice_number}</p>
                            <Button variant="ghost" size="sm" onClick={cancelInvoiceImport}>
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                          <div className="border rounded-lg overflow-hidden bg-white">
                            <Table>
                              <TableHeader>
                                <TableRow className="bg-slate-50">
                                  <TableHead className="w-12">Select</TableHead>
                                  <TableHead>Product</TableHead>
                                  <TableHead className="w-32">Original Qty</TableHead>
                                  <TableHead className="w-32">Order Qty</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {selectedInvoice.items.map((item, idx) => (
                                  <TableRow key={idx} className={partialSelections[idx]?.selected ? 'bg-teal-50' : ''}>
                                    <TableCell>
                                      <Checkbox
                                        checked={partialSelections[idx]?.selected || false}
                                        onCheckedChange={() => togglePartialSelection(idx)}
                                        data-testid={`partial-select-${idx}`}
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <p className="font-medium">{item.product_name}</p>
                                    </TableCell>
                                    <TableCell className="text-slate-500">{item.quantity}</TableCell>
                                    <TableCell>
                                      <Input
                                        type="number"
                                        min="1"
                                        value={partialSelections[idx]?.quantity || item.quantity}
                                        onChange={(e) => updatePartialQuantity(idx, e.target.value)}
                                        disabled={!partialSelections[idx]?.selected}
                                        className="w-24"
                                      />
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                          <div className="flex justify-end gap-2">
                            <Button variant="outline" onClick={cancelInvoiceImport}>Cancel</Button>
                            <Button 
                              onClick={applyPartialSelections}
                              className="bg-teal-600 hover:bg-teal-700"
                              data-testid="apply-partial-btn"
                            >
                              Add Selected Items
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Manual Product Addition */}
              {(editMode || formData.items.length > 0 || (formData.vendor_id && vendorInvoices.length === 0)) && (
                <div className="space-y-2">
                  <Label>Add Products Manually</Label>
                  <Select onValueChange={addItemToForm}>
                    <SelectTrigger data-testid="po-product-select">
                      <SelectValue placeholder="Search and add products..." />
                    </SelectTrigger>
                    <SelectContent>
                      {products.map(product => (
                        <SelectItem key={product.id} value={product.id}>
                          {product.name} - {product.brand} ({product.size})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Items List */}
              {formData.items.length > 0 && (
                <div className="space-y-2">
                  <Label>PO Items ({formData.items.length})</Label>
                  <div className="border rounded-lg overflow-hidden overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead>Product</TableHead>
                          <TableHead className="w-20">Qty</TableHead>
                          <TableHead className="w-28">Rate (₹)</TableHead>
                          <TableHead className="w-20">GST %</TableHead>
                          <TableHead className="w-28 text-right">Amount</TableHead>
                          <TableHead className="w-10"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {formData.items.map((item) => {
                          const amount = (parseFloat(item.quantity) || 0) * (parseFloat(item.rate) || 0);
                          return (
                            <TableRow key={item.product_id}>
                              <TableCell>
                                <div>
                                  <p className="font-medium text-sm">{item.product_name}</p>
                                  {(item.brand || item.size) && (
                                    <p className="text-xs text-slate-500">{item.brand} {item.brand && item.size && '|'} {item.size}</p>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="number"
                                  min="1"
                                  value={item.quantity}
                                  onChange={(e) => updateItemQuantity(item.product_id, e.target.value)}
                                  className="w-16 text-sm"
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={item.rate}
                                  onChange={(e) => updateItemField(item.product_id, 'rate', e.target.value)}
                                  placeholder="0.00"
                                  className="w-24 text-sm"
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="number"
                                  step="0.1"
                                  min="0"
                                  value={item.gst_percentage}
                                  onChange={(e) => updateItemField(item.product_id, 'gst_percentage', e.target.value)}
                                  className="w-16 text-sm"
                                />
                              </TableCell>
                              <TableCell className="text-right font-medium">
                                {amount > 0 ? `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '-'}
                              </TableCell>
                              <TableCell>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeItemFromForm(item.product_id)}
                                  className="text-red-500 hover:text-red-700 p-1"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                  
                  {/* Totals Summary */}
                  {formData.items.some(item => item.rate > 0) && (
                    <div className="flex justify-end mt-4">
                      <div className="bg-slate-50 p-4 rounded-lg w-64 space-y-2">
                        {(() => {
                          const subtotal = formData.items.reduce((sum, item) => 
                            sum + (parseFloat(item.quantity) || 0) * (parseFloat(item.rate) || 0), 0);
                          const gstTotal = formData.items.reduce((sum, item) => {
                            const amt = (parseFloat(item.quantity) || 0) * (parseFloat(item.rate) || 0);
                            return sum + amt * ((parseFloat(item.gst_percentage) || 12) / 100);
                          }, 0);
                          const grandTotal = subtotal + gstTotal;
                          return (
                            <>
                              <div className="flex justify-between text-sm">
                                <span className="text-slate-600">Subtotal:</span>
                                <span>₹{subtotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-slate-600">GST Total:</span>
                                <span>₹{gstTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                              </div>
                              <div className="flex justify-between font-bold text-teal-600 pt-2 border-t">
                                <span>Grand Total:</span>
                                <span>₹{grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Notes */}
              <div className="space-y-2">
                <Label>Notes (Optional)</Label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full min-h-[80px] px-3 py-2 border rounded-md text-sm resize-none focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Any additional notes..."
                  data-testid="po-notes-input"
                />
              </div>
            </div>
          </ScrollArea>

          <DialogFooter className="pt-4 border-t">
            <Button variant="outline" onClick={() => { resetForm(); setShowCreateModal(false); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreatePO} 
              className="bg-teal-600 hover:bg-teal-700" 
              data-testid="save-po-btn"
              disabled={!formData.vendor_id || formData.items.length === 0}
            >
              {editMode ? 'Update PO' : 'Create PO'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Modal */}
      <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Purchase Order Details</DialogTitle>
          </DialogHeader>

          {selectedPO && (
            <ScrollArea className="flex-1 px-1">
              <div className="space-y-6 py-2">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">PO Number</p>
                    <p className="font-semibold text-teal-600">{selectedPO.po_number}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Status</p>
                    <Badge variant="outline" className={cn('mt-1', statusColors[selectedPO.status])}>
                      {statusLabels[selectedPO.status]}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Vendor</p>
                    <p className="font-medium">{selectedPO.vendor_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">PO Date</p>
                    <p className="font-medium">{new Date(selectedPO.po_date).toLocaleDateString()}</p>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-slate-500 mb-2">Items</p>
                  <div className="border rounded-lg overflow-hidden overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead>Product</TableHead>
                          <TableHead className="text-center w-16">Qty</TableHead>
                          <TableHead className="text-right w-24">Rate</TableHead>
                          <TableHead className="text-center w-16">GST%</TableHead>
                          <TableHead className="text-right w-24">Amount</TableHead>
                          <TableHead className="text-center w-20">Received</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedPO.items.map((item, idx) => {
                          const rate = item.rate || item.unit_price || 0;
                          const amount = (item.quantity || 0) * rate;
                          const productName = item.product_name || item.item_name || 'Unknown';
                          return (
                            <TableRow key={idx}>
                              <TableCell>
                                <div>
                                  <p className="font-medium text-sm">{productName}</p>
                                  {(item.brand || item.size || item.category) && (
                                    <p className="text-xs text-slate-500">
                                      {item.category && !item.brand && !item.size ? item.category : ''}
                                      {item.brand} {item.brand && item.size && '|'} {item.size}
                                    </p>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="text-center">{item.quantity}</TableCell>
                              <TableCell className="text-right">
                                {rate ? `₹${rate.toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '-'}
                              </TableCell>
                              <TableCell className="text-center">
                                {rate ? `${item.gst_percentage || 5}%` : '-'}
                              </TableCell>
                              <TableCell className="text-right font-medium">
                                {amount > 0 ? `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '-'}
                              </TableCell>
                              <TableCell className="text-center">
                                <span className={item.received_quantity >= item.quantity ? 'text-green-600' : 'text-amber-600'}>
                                  {item.received_quantity || 0}
                                </span>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                {/* Totals */}
                {(selectedPO.subtotal > 0 || selectedPO.total_taxable > 0) && (
                  <div className="flex justify-end">
                    <div className="bg-slate-50 p-4 rounded-lg w-64 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Subtotal:</span>
                        <span>₹{(selectedPO.subtotal || selectedPO.total_taxable || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">GST Total:</span>
                        <span>₹{(selectedPO.total_gst || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                      </div>
                      <div className="flex justify-between font-bold text-teal-600 pt-2 border-t">
                        <span>Grand Total:</span>
                        <span>₹{(selectedPO.grand_total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                      </div>
                    </div>
                  </div>
                )}

                {selectedPO.notes && (
                  <div>
                    <p className="text-sm text-slate-500">Notes</p>
                    <p className="mt-1 text-sm bg-slate-50 p-3 rounded-lg">{selectedPO.notes}</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowViewModal(false)}>Close</Button>
            <Button onClick={() => handleDownloadPDF(selectedPO)} className="bg-teal-600 hover:bg-teal-700">
              <Download className="w-4 h-4 mr-2" />
              Download PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convert to Purchase Modal */}
      <Dialog open={showConvertModal} onOpenChange={setShowConvertModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Convert PO to Purchase</DialogTitle>
            <DialogDescription>
              Enter the purchase details for {selectedPO?.po_number}
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="flex-1 px-1">
            <div className="space-y-6 py-4">
              {/* Invoice Details */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Vendor Invoice Number *</Label>
                  <Input
                    value={convertData.vendor_invoice_number}
                    onChange={(e) => setConvertData(prev => ({ ...prev, vendor_invoice_number: e.target.value }))}
                    placeholder="e.g., INV-2024-001"
                    data-testid="convert-invoice-number"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Purchase Date *</Label>
                  <Input
                    type="date"
                    value={convertData.purchase_date}
                    onChange={(e) => setConvertData(prev => ({ ...prev, purchase_date: e.target.value }))}
                    data-testid="convert-purchase-date"
                  />
                </div>
              </div>

              {/* Items */}
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Product</TableHead>
                      <TableHead className="w-28">Qty</TableHead>
                      <TableHead className="w-32">Batch No. *</TableHead>
                      <TableHead className="w-32">Price *</TableHead>
                      <TableHead className="w-24">GST %</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {convertData.items.map((item) => (
                      <TableRow key={item.product_id}>
                        <TableCell>
                          <p className="font-medium">{item.product_name}</p>
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => updateConvertItem(item.product_id, 'quantity', e.target.value)}
                            className="w-20"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            value={item.batch_number}
                            onChange={(e) => updateConvertItem(item.product_id, 'batch_number', e.target.value)}
                            placeholder="Batch #"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            step="0.01"
                            value={item.purchase_price}
                            onChange={(e) => updateConvertItem(item.product_id, 'purchase_price', e.target.value)}
                            placeholder="0.00"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            step="0.1"
                            value={item.gst_percentage}
                            onChange={(e) => updateConvertItem(item.product_id, 'gst_percentage', e.target.value)}
                            className="w-20"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </ScrollArea>

          <DialogFooter className="pt-4 border-t">
            <Button variant="outline" onClick={() => setShowConvertModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleConvertToPurchase} className="bg-teal-600 hover:bg-teal-700" data-testid="confirm-convert-btn">
              <ArrowRightCircle className="w-4 h-4 mr-2" />
              Convert to Purchase
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PurchaseOrders;
